import React, { FC } from 'react';
import styled from 'styled-components';
import { Row, Table, TableHeading } from 'src/components/widgets/table';
import Stack from 'src/components/widgets/stack/stack';
import {
    SkeletonBlock,
    SkeletonFiltersRow,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonTable,
    SkeletonTableRow,
    SkeletonTabs
} from 'src/components/widgets/skeleton';

const ProductsSkeletonShell = styled(SkeletonPageShell)``;

const ProductsSkeletonHeader = styled(SkeletonPageHeader)``;

const ProductTaskStatsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(120px, 1fr));
    gap: 12px;
    min-width: 0;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const productsTableColumns =
    '45px 85px minmax(180px, 1.2fr) minmax(120px, 1fr) minmax(64px, 0.4fr) minmax(92px, 0.6fr) minmax(92px, 0.6fr) minmax(80px, 0.6fr) minmax(104px, 1fr)';

const ProductTableCellSkeleton = styled(SkeletonBlock)`
    max-width: 100%;
`;

const ProductsOptionalColumn = styled.div`
    @media (max-width: 900px) {
        display: none;
    }
`;

const TaskDetailsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
    min-width: 0;

    @media (max-width: 968px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const TaskSectionSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    padding: 24px;
    min-width: 0;
`;

const ProductInfoSkeletonRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }
`;

export const ProductInfoRowsSkeleton: FC = () => (
    <SkeletonStack aria-hidden='true' $gap='0'>
        {Array.from({ length: 4 }).map((_, index) => (
            <ProductInfoSkeletonRow key={index}>
                <SkeletonBlock $width='90px' $height='13px' />
                <SkeletonRow $gap='10px' $justify='flex-end'>
                    {index === 0 && <SkeletonBlock $width='40px' $height='40px' $radius='4px' />}
                    <SkeletonBlock $width={index === 0 ? '160px' : '92px'} $height='13px' />
                </SkeletonRow>
            </ProductInfoSkeletonRow>
        ))}
    </SkeletonStack>
);

export const ProductTableRowsSkeleton: FC<{ rows?: number }> = ({ rows = 8 }) => (
    <>
        {Array.from({ length: rows }).map((_, index) => (
            <Row
                key={index}
                cells={[
                    <ProductTableCellSkeleton $width='18px' $height='18px' $radius='4px' />,
                    <ProductTableCellSkeleton $width='48px' $height='48px' $radius='8px' />,
                    <SkeletonStack $gap='8px'>
                        <ProductTableCellSkeleton $width={index % 2 === 0 ? '76%' : '58%'} $height='16px' />
                        <ProductTableCellSkeleton $width='42%' $height='12px' />
                    </SkeletonStack>,
                    <ProductTableCellSkeleton $width={index % 2 === 0 ? '72%' : '54%'} $height='14px' />,
                    <ProductsOptionalColumn>
                        <ProductTableCellSkeleton $width='82px' $height='20px' $radius='10px' />
                    </ProductsOptionalColumn>,
                    <ProductsOptionalColumn>
                        <ProductTableCellSkeleton $width='24px' $height='24px' $round />
                    </ProductsOptionalColumn>,
                    <ProductsOptionalColumn>
                        <ProductTableCellSkeleton $width='92px' $height='24px' $radius='12px' />
                    </ProductsOptionalColumn>,
                    <ProductsOptionalColumn>
                        <ProductTableCellSkeleton $width='78px' $height='24px' $radius='12px' />
                    </ProductsOptionalColumn>,
                    <ProductsOptionalColumn>
                        <ProductTableCellSkeleton $width='42px' $height='14px' />
                    </ProductsOptionalColumn>,
                    <SkeletonRow $gap='8px' $justify='flex-end'>
                        <ProductTableCellSkeleton $width='28px' $height='28px' $radius='4px' />
                        <ProductTableCellSkeleton $width='28px' $height='28px' $radius='4px' />
                    </SkeletonRow>
                ]}
            />
        ))}
    </>
);

export const ProductTableSkeleton: FC<{ headings: TableHeading[]; rows?: number }> = ({ headings, rows }) => (
    <Table headings={headings} data-testid='table-loading' compact>
        <ProductTableRowsSkeleton rows={rows} />
    </Table>
);

export const ProductsPageSkeleton: FC = () => (
    <ProductsSkeletonShell aria-hidden='true'>
        <ProductsSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(260px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonRow $gap='8px'>
                <SkeletonBlock $width='132px' $height='36px' $radius='6px' />
                <SkeletonBlock $width='140px' $height='36px' $radius='6px' />
                <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
            </SkeletonRow>
        </ProductsSkeletonHeader>
        <SkeletonTabs tabWidths={['72px', '124px']} />
        <Stack $gap='16px'>
            <SkeletonBlock $width='100%' $height='70px' $radius='6px' />
            <Stack $gap='8px'>
                <SkeletonFiltersRow controlWidths={['214px', '214px', '214px', '214px', '300px']} $margin='0' />
                <SkeletonTable>
                    <SkeletonTableRow $columns={productsTableColumns} $hideLastBorder={false}>
                        <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                        <SkeletonBlock $width='1px' $height='12px' />
                        <SkeletonBlock $width='42px' $height='12px' />
                        <SkeletonBlock $width='38px' $height='12px' />
                        <SkeletonBlock $width='28px' $height='12px' />
                        <SkeletonBlock $width='42px' $height='12px' />
                        <SkeletonBlock $width='44px' $height='12px' />
                        <SkeletonBlock $width='56px' $height='12px' />
                        <SkeletonBlock $width='1px' $height='12px' />
                    </SkeletonTableRow>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <SkeletonTableRow key={index} $columns={productsTableColumns}>
                            <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                            <SkeletonBlock $width='48px' $height='48px' $radius='8px' />
                            <SkeletonStack $gap='8px'>
                                <SkeletonBlock $width={index % 2 === 0 ? '76%' : '58%'} $height='16px' />
                                <SkeletonBlock $width='42%' $height='12px' />
                            </SkeletonStack>
                            <SkeletonBlock $width={index % 2 === 0 ? '72%' : '54%'} $height='14px' />
                            <ProductsOptionalColumn>
                                <SkeletonBlock $width='24px' $height='24px' $round />
                            </ProductsOptionalColumn>
                            <ProductsOptionalColumn>
                                <SkeletonBlock $width='92px' $height='24px' $radius='12px' />
                            </ProductsOptionalColumn>
                            <ProductsOptionalColumn>
                                <SkeletonBlock $width='78px' $height='24px' $radius='12px' />
                            </ProductsOptionalColumn>
                            <ProductsOptionalColumn>
                                <SkeletonBlock $width='42px' $height='14px' />
                            </ProductsOptionalColumn>
                            <SkeletonRow $gap='8px' $justify='flex-end'>
                                <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                                <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                            </SkeletonRow>
                        </SkeletonTableRow>
                    ))}
                </SkeletonTable>
            </Stack>
        </Stack>
    </ProductsSkeletonShell>
);

export const ProductDuplicationTaskDetailPageSkeleton: FC = () => (
    <ProductsSkeletonShell aria-hidden='true'>
        <ProductsSkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(320px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='92px' $height='36px' $radius='6px' />
        </ProductsSkeletonHeader>
        <SkeletonStack $gap='18px'>
            <TaskSectionSkeleton>
                <SkeletonStack $gap='14px'>
                    <SkeletonBlock $width='110px' $height='20px' />
                    <ProductTaskStatsGridSkeleton>
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                        <SkeletonBlock $height='64px' $radius='8px' />
                    </ProductTaskStatsGridSkeleton>
                </SkeletonStack>
            </TaskSectionSkeleton>
            <TaskSectionSkeleton>
                <SkeletonBlock $width='100px' $height='20px' $margin='0 0 18px' />
                <TaskDetailsGridSkeleton>
                    <ProductInfoRowsSkeleton />
                    <ProductInfoRowsSkeleton />
                </TaskDetailsGridSkeleton>
            </TaskSectionSkeleton>
            <TaskSectionSkeleton>
                <SkeletonStack $gap='12px'>
                    <SkeletonBlock $width='92px' $height='20px' />
                    <SkeletonBlock $height='12px' $radius='6px' />
                    <SkeletonBlock $width='min(520px, 100%)' $height='44px' $radius='6px' />
                </SkeletonStack>
            </TaskSectionSkeleton>
        </SkeletonStack>
    </ProductsSkeletonShell>
);
